import type {
  BookableOpening,
  CreateOpeningsResponse,
  MyOpening,
  OpeningSkipReason,
  ScheduleEntry,
} from "@edu/shared";

/**
 * Окна записи в сетке расписания: чистая часть (`docs/booking-design.md`, 3.2).
 *
 * Окно это не занятие, и строкой сетки (`ScheduleEntry`) не становится: у него
 * нет ни состояния, ни собеседника, ни урока. Но стоит оно на том же времени и
 * обязано делить с занятиями и диапазон часов, и ширину колонки. Поэтому
 * раскладка получает ОДИН список, где окна едут рядом с занятиями под своим
 * пространством ключей (`w:`), а колонка разводит их по ключу обратно.
 *
 * ⚠️ Роль считается по ОКНУ, а не по аккаунту: человек может и учить, и
 * учиться (1.3 проекта). Свои окна приезжают в `myOpenings`, окна его
 * преподавателей в `bookableOpenings`, и в одной неделе бывают оба списка.
 *
 * Своим файлом без импорта `common.ts`: тот тянет клиент API, а здесь чистые
 * функции под `node:test`. Даты в слова переводит вызывающий.
 */

/** Префикс ключа окна. Ключи строк сетки начинаются иначе (`o:`, `l:`). */
const KEY_PREFIX = "w:";

/** Окно, как его рисует сетка. `mine` разводит две роли. */
export type GridOpening =
  | {
      key: string;
      id: string;
      at: number;
      durationMin: number;
      mine: true;
      /** Поверх окна стоит занятие преподавателя: запись закроется на первой попытке. */
      covered: boolean;
    }
  | {
      key: string;
      id: string;
      at: number;
      durationMin: number;
      mine: false;
      teacherName: string;
      /**
       * Писать ли имя в коробке сетки. Нет, когда у всех окон недели
       * преподаватель один: имя было бы лишней строкой в узкой коробке. В
       * раскрытой карточке имя стоит всегда.
       */
      named: boolean;
    };

export function isOpeningKey(key: string): boolean {
  return key.startsWith(KEY_PREFIX);
}

/**
 * Окна недели для сетки: свои и те, на которые можно записаться.
 *
 * ⚠️ Имя преподавателя показывается, только если преподавателей в окнах
 * недели больше одного. Считается по ВСЕЙ неделе, а не по видимым колонкам:
 * иначе имя появлялось бы и пропадало при листании по дням.
 */
export function gridOpenings(
  mine: readonly MyOpening[],
  bookable: readonly BookableOpening[],
): GridOpening[] {
  const names = new Set(bookable.map((o) => o.teacherName));
  const many = names.size > 1;
  return [
    ...mine.map(
      (o): GridOpening => ({
        key: `${KEY_PREFIX}${o.id}`,
        id: o.id,
        at: o.startsAt,
        durationMin: o.durationMin,
        mine: true,
        covered: o.covered,
      }),
    ),
    ...bookable.map(
      (o): GridOpening => ({
        key: `${KEY_PREFIX}${o.id}`,
        id: o.id,
        at: o.startsAt,
        durationMin: o.durationMin,
        mine: false,
        teacherName: o.teacherName,
        named: many,
      }),
    ),
  ];
}

/**
 * Что раскладывает календарь: занятия и окна одним списком.
 *
 * ⚠️ Окна входят и в диапазон часов, и в дорожки. Иначе окно в 8:00 при
 * занятиях с 14:00 стояло бы над верхним краем, а окно поверх занятия
 * рисовалось бы с ним внахлёст, и одно из двух нельзя было бы нажать.
 * Состояния у окна нет (`null`), значит место оно занимает, как живое занятие.
 */
export function calendarItems(
  entries: readonly Pick<ScheduleEntry, "key" | "at" | "durationMin" | "state">[],
  openings: readonly GridOpening[],
): { key: string; at: number; durationMin: number | null; state: ScheduleEntry["state"] }[] {
  return [
    ...entries.map((e) => ({ key: e.key, at: e.at, durationMin: e.durationMin, state: e.state })),
    ...openings.map((o) => ({ key: o.key, at: o.at, durationMin: o.durationMin, state: null })),
  ];
}

/**
 * Пуста ли неделя.
 *
 * ⚠️ Окна считаются: неделя, где у ученика одно свободное время и ни одного
 * занятия, не пустая, и текст «занятий нет, они появятся» был бы неправдой ровно
 * тогда, когда записаться можно прямо сейчас.
 */
export function weekIsEmpty(entryCount: number, openings: readonly GridOpening[]): boolean {
  return entryCount === 0 && openings.length === 0;
}

/** Причина, по которой дата пачки не открылась, словами преподавателя. */
export const SKIP_REASON_TEXT: Readonly<Record<OpeningSkipReason, string>> = {
  teacher_busy: "у вас в это время занятие",
  already_open: "это время уже открыто для записи",
  no_such_time: "в этот день часы переводят, и такого времени нет",
  past: "это время уже прошло",
};

/**
 * Исход открытия времени: что сказать в самом диалоге.
 *
 * ⚠️ Строк бывает до шестидесяти, поэтому это список в диалоге, а не всплывающая
 * полоса (3.2 проекта). Первой строкой счёт, потом по строке на каждую
 * неоткрытую дату: пропуск недели в пачке это дырка в опубликованном
 * расписании, и преподаватель обязан видеть, какая именно.
 */
export function openingsOutcome(
  res: CreateOpeningsResponse,
  dayTitle: (date: string) => string,
): { head: string; lines: string[] } {
  const opened = res.created.length;
  const total = opened + res.skipped.length;
  const lines = res.skipped.map((s) => `${dayTitle(s.date)}: ${SKIP_REASON_TEXT[s.reason]}`);
  // Одна дата: счёт «0 из 1» читался бы задачкой, говорим прямо
  if (total === 1 && opened === 0) {
    const only = res.skipped[0];
    return {
      head: `Время не открыто: ${only === undefined ? "" : SKIP_REASON_TEXT[only.reason]}.`,
      lines: [],
    };
  }
  return { head: `Открыто для записи ${String(opened)} из ${String(total)}. Не открыто:`, lines };
}

/**
 * Пустая неделя ученика словами.
 *
 * ⚠️ Две редакции, и вторая существует потому, что первая при окнах врёт:
 * «занятия появятся, как только преподаватель их поставит» ученику, которому
 * открыто время, говорит «делать нечего», хотя записаться можно прямо сейчас.
 */
export const BOOK_HINT = "Можно записаться на свободное время: нажмите на него в календаре.";

export function studentEmptyWeekText(canBook: boolean): string {
  return canBook
    ? `Занятий на этой неделе нет. ${BOOK_HINT}`
    : "Занятий на этой неделе нет. Расписание ведёт преподаватель: занятия появятся здесь, как только он их поставит.";
}
